/*!
 * Copyright © 2023. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { delay, filter, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LiveChatScriptLoaderService } from '../../external/services';
import { IDeviceHealthCheckLiveChatParams } from '../models';

@Injectable()
export class DeviceHealthCheckLiveChatService {
  showChatDelay = 5000;
  private readonly _showChatPanel$ = new Subject<void>();

  constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _liveChatScriptLoaderService: LiveChatScriptLoaderService
  ) {
    this._showChatPanel$.pipe(delay(this.showChatDelay)).subscribe(() => {
      // Delay to allow the chat to initialize properly
      (this._document.defaultView as any).icPatronChat.show();
    });
  }

  public openChat(params: IDeviceHealthCheckLiveChatParams): void {
    this._liveChatScriptLoaderService.loadScript();
    this._liveChatScriptLoaderService.isLoaded$
      .pipe(
        filter(loaded => loaded),
        take(1)
      )
      .subscribe(() => {
        this._closeExistingChat();
        this._openChat(params);
      });
  }

  private _closeExistingChat(): void {
    this._document.querySelector('.ie-div-position-customer-chat')?.remove();
  }

  private _openChat(params: IDeviceHealthCheckLiveChatParams): void {
    const formattedParams = [
      'Vehicle tracker is not reporting correctly. Troubleshooting attempted using Device Health Check.',
      `  • Account ID: ${params.accountId}`,
      `  • Vehicle Name: ${params.vehicleName}`,
      `  • Tracker Type: ${params.trackerType}`,
      `  • ESN: ${params.ESN}`,
      `  • Last Update: ${params.lastUpdate}`,
      `  • Status: ${params.status}`
    ];

    (this._document.defaultView as any).icPatronChat.init({
      serverHost: environment.config.deviceHealthCheckLiveChat.serverHost,
      bus_no: parseInt(environment.config.deviceHealthCheckLiveChat.busNo, 10),
      poc: environment.config.deviceHealthCheckLiveChat.poc,
      params: formattedParams
    });

    const chatReadyIntervalId = setInterval(() => {
      const ready = this._document.querySelector('#icChatButton');
      if (ready) {
        clearInterval(chatReadyIntervalId);
        this._showChatPanel$.next();
      }
    }, 500);
  }
}
